import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { UuidHelper } from '../../helpers/uuid.helper';

/**
 * One collapsible region: open/closed state plus a stable id for the
 * `aria-controls` ↔ region pairing. First user: the issue dialog's creator +
 * audit-log block, collapsed/expanded by clicking the bar above it (Anke,
 * 2026-09-25: "bei Klick … auf Höhe 0 schrumpfen und bei nochmaligem Klick
 * wieder zurück", starting collapsed whenever the dialog opens). Second
 * user, on the same host: the issue dialog's sidebar toggle for phone width
 * (Anke, 2026-10-18) — one controller per region. That region spans two
 * elements, so the dialog uses `regionId` as an id prefix
 * (`${regionId}-header`, `${regionId}-main`).
 *
 * A Reactive Controller rather than a field on the host — Composition, not
 * Inheritance, per ADR-007 §2/§6, the same reasoning as
 * `JustMovedFlashController`. It keeps the state testable without mounting
 * the host: the issue dialog resolves half a dozen services through
 * `container.resolve`, which a component test would all have to fake.
 *
 * The state lives exactly as long as the host instance. The issue dialog is
 * opened through `UiHelper.openDialogEntitiesEdit`, which creates a new dialog
 * element on every open, so a reopened dialog starts from `initiallyCollapsed`
 * again, while switching tabs inside one dialog keeps it. That promise only
 * holds for hosts created per open — `DialogHelper.showDialog` reuses an
 * already open dialog of the same type, which then keeps its state.
 *
 * A plain private field, NOT `@state()` — this controller is not a Lit
 * element itself, so `toggle()` calls `host.requestUpdate()` explicitly.
 *
 * Deliberately extracted while it had a single user, for testability — not the usual
 * extract-after-duplication case (compare `JustMovedFlashController`, pulled
 * out after its third copy). No `hostConnected()`: it is optional in Lit and
 * this controller has nothing to set up or tear down.
 *
 * Not (yet) used by `ha-audit-log-viewer` (`_isOpen`) or
 * `knowledge-sidebar-list` (`_assetsExpanded`): their toggles also trigger a
 * load, so they are not pure visibility switches.
 *
 * @example
 * ```ts
 * class MyDialog extends AbstractLitElement {
 *     private readonly _details = new CollapseToggleController(this, true);
 *
 *     render() {
 *         return html`
 *             <div role="button" tabindex="0"
 *                  aria-expanded=${!this._details.collapsed}
 *                  aria-controls=${this._details.regionId}
 *                  @click=${this._details.toggle}
 *                  @keydown=${KeyboardActivationHelper.onEnterOrSpace(this._details.toggle)}></div>
 *             <div id=${this._details.regionId}
 *                  class=${classMap({'is-collapsed': this._details.collapsed})}>…</div>`;
 *     }
 * }
 * ```
 */
export class CollapseToggleController implements ReactiveController {

    /** Stable id for `aria-controls` ↔ the collapsible region; one per controller instance. */
    public readonly regionId = `collapse-${UuidHelper.generateV7()}`;

    private _collapsed: boolean;

    /**
     * Never assigned (type-only declaration, no emitted code): lit's `ReactiveController`
     * consists of optional hooks only, so TypeScript's weak-type check rejects a class that
     * shares none of them. Declaring one as optional keeps the runtime object unchanged —
     * lit calls `hostConnected?.()`, which stays a no-op.
     */
    declare public readonly hostConnected?: () => void;

    constructor(private readonly host: ReactiveControllerHost, initiallyCollapsed = false) {
        this._collapsed = initiallyCollapsed;
        host.addController(this);
    }

    public get collapsed(): boolean {
        return this._collapsed;
    }

    /** Arrow field on purpose: a stable reference that can be handed to a
     *  template or a callbacks object without re-binding on every render. */
    public readonly toggle = (): void => {
        this._collapsed = !this._collapsed;
        this.host.requestUpdate();
    };
}
